import { use, useCallback } from "react";
import type { MapsEvent } from "../../types/MapsEvent";
import { MapsContext } from "../../useMaps";
import { useSendTrackingEvent } from "./useSendTrackingEvent";

const eventsThatShouldOnceBeSendOnce: string[] = [];

export function useSendEvent(): (event: MapsEvent) => void {
    const sendTrackingEvent = useSendTrackingEvent();

    const mapsContext = use(MapsContext);

    if (mapsContext === undefined) {
        throw new Error(
            "useSendEvent() was called before MapsContext value was properly initialized.",
        );
    }

    const {
        mapsProps: { onEvent },
    } = mapsContext;

    return useCallback(
        (event: MapsEvent) => {
            // don't send events that already has been thrown
            if (eventsThatShouldOnceBeSendOnce.includes(event.type)) return;

            // Transform event into simple javascript object because sometimes it is partially a javascript proxy (from valtio) under the hood.
            // Javascript proxies cannot be serialized when they are passed to the native apps via the bridge because of framework limitations
            // the apps use for webview <-> app communication.
            const eventAsSimpleObject = JSON.parse(JSON.stringify(event));

            onEvent?.(eventAsSimpleObject);

            // send tracking events for some of the normal events generated as well
            switch (event.type) {
                case "mapStyleChanged":
                    sendTrackingEvent({ name: "change_basemap_click" });
                    break;

                case "animationStarted":
                    sendTrackingEvent({ name: "play" });
                    break;

                case "animationStopped":
                    sendTrackingEvent({ name: "pause" });
                    break;

                case "refreshData":
                    sendTrackingEvent({ name: "refresh" });
                    break;

                case "shownLayersChanged": {
                    sendTrackingEvent({
                        // @ts-expect-error TODO: remove when we support payloads for google analytics events
                        name: `shown_layers_changed_${event.toLayers.join(",")}`,
                        payload: {
                            toLayers: event.toLayers.join(","),
                        },
                    });
                    break;
                }

                case "geolocateUser":
                    sendTrackingEvent({ name: "center_on_user_button_click" });
                    break;

                case "mapPan":
                    sendTrackingEvent({ name: "map_pan" });
                    break;

                case "popupClose":
                    sendTrackingEvent({ name: "popup_close" });
                    break;

                case "pinSwitchActiveLocation":
                    sendTrackingEvent({ name: "pin_switch_active_location" });
                    break;

                case "sliderMove":
                    sendTrackingEvent({ name: "slider_move" });
                    break;

                case "legendClick":
                    sendTrackingEvent({ name: "legend_click" });
                    break;

                case "searchClick":
                    sendTrackingEvent({ name: "search_click" });
                    break;

                case "animationSpeedMultiplierChanged": {
                    sendTrackingEvent({
                        name: "animation_speed_multiplier_changed",
                        payload: {
                            toMultiplier: event.toMultiplier,
                        },
                    });
                    break;
                }

                case "playHourlyForecastClick":
                    sendTrackingEvent({ name: "play_hourly_forecast_click" });
                    break;

                case "productDropdownClick":
                    sendTrackingEvent({ name: "product_dropdown_click" });
                    break;

                case "sliderIsReadyToUse":
                    eventsThatShouldOnceBeSendOnce.push("sliderIsReadyToUse");
                    break;

                case "mapLoaded":
                    eventsThatShouldOnceBeSendOnce.push("mapLoaded");
                    break;

                case "uiReadyToBeDisplayed":
                    eventsThatShouldOnceBeSendOnce.push("uiReadyToBeDisplayed");
                    break;

                case "expandSizeButtonClicked":
                case "restoreSizeButtonClicked":
                case "timelineMovedOverNow":
                case "wcomLogoClick":
                case "locationsPopupVisibilityChanged":
                case "onDebugDataChange":
                case "timeFrameChanged":
                    break;
            }
        },
        [onEvent, sendTrackingEvent],
    );
}
